// src/utils/catalog/catalogChangelogData.js
// Changelog for the catalog Deck Builder (/catalog). Separate from the 4.x app.
// Catalog uses 1.0 / 0.9 / 0.8 (.##). The homepage stays on 4.x.

const catalogChangelogData = [
    {
        version: '1.1',
        title: 'Custom art variants',
        date: '2026-08-26',
        isOpen: true,
        changes: [
            {
                title: 'Deck & identity',
                items: [
                    'FIXED: Custom / CDN / remote JSON printings with null collector numbers no longer collapse onto `set-0-std`. Each printing keeps a unique `number` / `localId` / catalog id.',
                    'FIXED: Art sliders no longer auto-group every same-name card. Without `variantGroup`, family is name + collector number + tag/rule-box. With `variantGroup`, that pool stacks even when numbers differ (#005 and #006).',
                    'FIXED: Same-number art printings no longer collapse to one catalog id, so both Mega Gardevoir arts import. Gallery **Stack** shows one tile with arrows; **Separate** shows every row without doubling the slider.',
                    'ADDED: Import/export (`tcg-deck-builder-export` 1.1) writes `number`, `localId`, `setNumber`, `set`, and keeps authored `variantGroup` / `variantLabel` only when present.',
                    'CHANGED: Malie foil/alt printings import as separate rows instead of being dropped.',
                    'ADDED: Import **Save as tab** writes a Custom sub-tab to the right of Pokecardmaker. Import preview stays under Import. **Clear** empties the preview; **Refresh** re-reads the JSON or saved copy. **Remove tab** deletes a saved set.',
                    'ADDED: Set JSON `toast` is the import popup only. `description` stays in About this set and above the gallery. `notes` stay on the set (hideable). Three separate fields; description no longer doubles as the popup.',
                    'CHANGED: Pocket-style HTML/PDF explain `toast` (import popup), `description` (About this set + gallery), and `notes` (hideable list) as three separate root fields.',
                    'CHANGED: Import help is a hover tooltip on the Import tab (and on Save as tab / Clear) instead of a full row of body text.',
                    'CHANGED: Import is one toolbar row: Import JSON, Upload, **Add tab** (expands tab name + Save as tab), Refresh, Clear. Export is a one-row source/filename/download bar.',
                    'ADDED: Hover a catalog card for 3 seconds to preview it, or double-click to pin a simple lightbox. Starting a drag cancels the preview so pickup is not frozen.',
                    'FIXED: Dragging imported custom art into the deck no longer replaces the card with a blank, blue, or purple tile. Pokecardmaker images keep their PNG `?format=webp` URL; the deck looks up the in-memory printing instead of rewriting `.png` to a missing `.webp` file.',
                    'ADDED: Custom **Test JSONs** tab with a Mega JSON / Emilia JSON dropdown. Load imports the selected sample; Download saves that file. **Guide** opens the Pocket-style HTML. **Template** is its own tab (download only, no import) with Guide and PDF. Import no longer hosts those sample buttons.',
                    'FIXED: Gallery art arrows no longer snap back to art 1 after a re-render, so `+` can add variation 2 as its own deck row.',
                    'FIXED: Same name + number + tags share one 4-of across arts (2+2, 3+1). Deck arrows move one copy to another picture instead of replacing the whole row. Bypass 4-of still raises that cap.',
                    'FIXED: PCM slugs that contain `-mega-` no longer collapse two same-number arts onto one catalog id, so Mega Gardevoir Art A and Art B can both sit in the deck.'
                ]
            },
            {
                title: 'Technical Improvements',
                items: [
                    'ADDED: Support module `src/utils/catalog/cardVariants.js` (unique collector numbers + `customVariants`).',
                    'ADDED: Support module `src/utils/catalog/savedCustomSets.js` (IndexedDB saved import tabs).',
                    'ADDED: Support modules `src/utils/catalog/setCopy.js`, `CatalogSetNotes.js`, `CatalogCardLightbox.js`.',
                    'ADDED: Support module `src/utils/catalog/catalogCardMemory.js` (catalogId lookup so deck add/drop reuses imported image URLs).',
                    'ADDED: Support modules `src/utils/catalog/testJsonSamples.js` (built-in howto JSON + blank template) and `deckCopyLimit.js` (shared 4-of across arts).',
                    'ADDED: JSON field encyclopedia `src/utils/catalog/JSON_FIELDS.md` (every accepted wrapper/card key for custom, Malie, Official, PCM, CDN). Support: `jsonFields.js`.',
                    'ADDED: Shareable Pocket-style explainer `public/guides/pocket-style/` (HTML + PDF with Test JSON card art). Support: `pocketStyleGuide.js`.',
                    'ADDED: Nested `arts` / `variants` / `printings` / `extraImages` on one card object expand to one row per picture (`expandNestedPrintings.js`). Celebi example: `src/data/catalog/nested-arts-celebi.json`.',
                    'ADDED: Local Emilia Forte Test JSON (`emilia-forte-showcase.json`). Four matching Fortes share `variantGroup` arts; three text Fortes stay separate; standard/full-art stack. Tag `FORTE` is the custom EX-style rule box.',
                    'ADDED: Support module `src/utils/catalog/pcmTags.js` maps PCM `subtypeId` (ex / Mega ex flags + name icon) onto catalog `tags`. PCM usually omits those words from `name`.',
                    'FIXED: `webpCdn.js` only rewrites Meta-PTCG CDN PNG/JPEG to WebP. `png?format=webp` customcardmaker URLs are left alone; applying WebP twice no longer clobbers `images.original`.',
                    'DEBUG: `/catalog?debug=1` lists custom art slider/family counts, stacked vs separate, saved import tab names, and set-copy toast/description/notes flags.'
                ]
            }
        ]
    },
    {
        version: '1.0',
        title: 'Catalog Deck Builder',
        date: '2026-08-25',
        isOpen: false,
        changes: [
            {
                title: 'Major Features',
                items: [
                    'ADDED: Catalog gallery with a collapsible deck drawer (starts at half screen).',
                    'ADDED: Custom tab with Import, Export, CDN Remote, and Pokecardmaker.',
                    'ADDED: **Official** tab with a TCG / Pocket slider and set dropdown. Cards come from TCGdex (`api.tcgdex.net`).',
                    'CHANGED: Official uses **one search bar**. Typing filters the loaded set; Enter searches all TCG or Pocket names. The extra Filter cards field is gone on Official (CDN and Custom still have it).',
                    'ADDED: Official **Filters** lightbox with homepage-style card type, energy, subtype, rarity, and HP chips. Match and sort live there. TCG / Pocket and the set dropdown stay on the toolbar.',
                    'ADDED: CDN set tabs: pinned sets first, first 10 visible, overflow in More.',
                    'ADDED: WebP-first card images from cdn.meta-ptcg.org (PNG/JPEG fallback).',
                    'ADDED: **Bypass 4-of** so copies can exceed the usual 4-of rule (60-card deck cap still applies).',
                    'ADDED: Pokecardmaker set-URL import, recent URLs, and a local IndexedDB cache.',
                    'ADDED: Paste a GitHub or JSON URL in CDN Remote or Import to load a remote set.',
                    'ADDED: Floating toasts: blue for success and loading, red for errors.',
                    'CHANGED: Empty deck hint is a floating note in the drop area.',
                    'CHANGED: Catalog chrome is not text-selectable. Filter and other fields remain selectable.',
                    'CHANGED: Leave-page prompt appears only when a catalog deck or import would be lost. Don\'t show again is optional.',
                    'FIXED: Catalog drawer, PCM, and CSV/Import buttons wrap on small screens instead of overlapping.'
                ]
            },
            {
                title: 'Deck & identity',
                items: [
                    'ADDED: Deck CSV is TCG Simulator `QTY,Name,Type,URL`.',
                    'FIXED: Same-name cards with different rule boxes (ex, Mega, V, ACE SPEC) stay distinct.',
                    'CHANGED: Hidden WIP CDN sets (SetName, TestMeta, meta-ptcg) are omitted from tabs and search.',
                    'ADDED: Deck-row **Pocket variation** slider when a Pocket name has 2+ prints.'
                ]
            },
            {
                title: 'Technical Improvements',
                items: [
                    'ADDED: Support modules in `src/utils/catalog/` (CDN, identity, PCM, remote JSON, CSV, Official, filters).',
                    'ADDED: `src/utils/catalog/officialCardFilters.js` and `OfficialFiltersLightbox.js`.',
                    'DEBUG: Open `/catalog?debug=1` for tab counts, hidden sets, PCM cache, remote URL, official filters, and leave-prompt dismiss.',
                    'DEBUG: Notes in `src/components/catalog/NOTES.md`.',
                    'FIXED: Cloudflare `/catalog` rewrite no longer 308s to the homepage. Support: `public/_redirects` (`catalog.html` / `grid.html` only, no `/*` splat), `wrangler.jsonc`, `scripts/ensure-spa-routes.js`.'
                ]
            }
        ]
    },
    {
        version: '0.9',
        title: 'Official types and Pocket art',
        date: '2026-08-25',
        isOpen: false,
        changes: [
            {
                title: 'Official TCG / Pocket',
                items: [
                    'FIXED: Official cards now use TCGdex **Pokémon / Trainer / Energy** (set lists omit `category`; types are filled from category queries). CSV Type and Energy 4-of follow that supertype.',
                    'FIXED: Pocket sets without TCGdex art (Paldean Wonders and similar) load Limitless images. Fake `assets.tcgdex.net` URLs are not invented. If every image fails, a typed name placeholder is shown.',
                    'ADDED: Deck-row **Pocket variation** slider when the loaded Pocket set has 2+ prints of the same name. The gallery TCG / Pocket slider is unchanged. Hidden when there is only one print.'
                ]
            },
            {
                title: 'Technical Improvements',
                items: [
                    'CHANGED: Support module `src/utils/catalog/officialTcgdexService.js` hydrates types and builds the TCGdex then Limitless image chain.',
                    'DEBUG: `/catalog?debug=1` lists official type counts, image host, missing TCGdex art, and Pocket variant-name count.'
                ]
            }
        ]
    },
    {
        version: '0.8',
        title: 'Official TCG and Pocket',
        date: '2026-08-25',
        isOpen: false,
        changes: [
            {
                title: 'Major Features',
                items: [
                    'ADDED: **Official** tab with a TCG / Pocket slider. Cards come from TCGdex (`api.tcgdex.net`). Pocket is series `tcgp`; TCG is every other set.',
                    'ADDED: Official set dropdown and name search (first 60 matches for the selected side).',
                    'ADDED: Official images use `assets.tcgdex.net` high.webp with PNG fallback. Catalog-only; the homepage search is unchanged.'
                ]
            },
            {
                title: 'Technical Improvements',
                items: [
                    'ADDED: Support module `src/utils/catalog/officialTcgdexService.js`.',
                    'DEBUG: `/catalog?debug=1` lists official mode, set, card count, and source.'
                ]
            }
        ]
    }
];

export const catalogLatestVersion = catalogChangelogData[0];

export default catalogChangelogData;
